import { createApp } from "petite-vue";
import Swiper from 'swiper';
import { Navigation } from 'swiper/modules';
import lightGallery from 'lightgallery';
import lgThumbnail from 'lightgallery/plugins/thumbnail';
import lgVideo from 'lightgallery/plugins/video';

import initArchiveOffer from "./archive-offer";
import initArchiveLoader from "./archive-loader";

if (document.querySelector('[data-map]')) {
    import("./map");
}

import.meta.glob(['@images/**']);
import.meta.env.DEV && import("@styles/main.sass");

interface Header {
    opened: boolean

    handleOpenedHeader(state: boolean): void
}

const headerEl = document.querySelector('[data-header]'),
    headerScope: Header = {
        opened: false,
        handleOpenedHeader(state) {
            document.body.classList.toggle("u-hiddenOverflow", state)
        }
    }

headerEl && createApp(headerScope).mount(headerEl);
initArchiveOffer();

// --------------------------------- Sticky header on scroll
(() => {
    const header = document.querySelector<HTMLElement>('[data-header]');
    if (!header) return;

    const logoImg = header.querySelector<HTMLImageElement>('[data-logo-dark]');
    const logoDark = logoImg?.dataset.logoDark || '';
    const logoDefault = logoImg?.dataset.logoDefault || '';

    const updateSticky = () => {
        // Skip sticky logic when mobile menu is open
        if (header.classList.contains('is-opened')) return;

        const scroll = window.scrollY;
        const currentIsSticky = header.classList.contains('is-sticky');

        // Hysteresis: turn on at 100, turn off at 50 to prevent flickering
        let shouldBeSticky = currentIsSticky;
        if (scroll > 100) {
            shouldBeSticky = true;
        } else if (scroll < 50) {
            shouldBeSticky = false;
        }

        if (shouldBeSticky !== currentIsSticky) {
            header.classList.toggle('is-sticky', shouldBeSticky);

            // Toggle logos based on sticky state
            if (logoImg) {
                const targetLogo = shouldBeSticky ? logoDark : logoDefault;
                if (targetLogo && logoImg.src !== targetLogo) {
                    logoImg.src = targetLogo;
                }
            }
        }
    };

    updateSticky();

    let ticking = false;
    window.addEventListener('scroll', () => {
        if (!ticking) {
            window.requestAnimationFrame(() => {
                updateSticky();
                ticking = false;
            });
            ticking = true;
        }
    }, { passive: true });
})();

//----------------------------------------

document.addEventListener("DOMContentLoaded", () => {
    initArchiveLoader();
});

// --------------------------- Count-up (HomeAbout + HomeHero)
(() => {
    const DURATION = 2400; // ms
    const easeOutQuad = (t: number) => t * (2 - t);

    function findTargets(root: ParentNode | Document = document): HTMLElement[] {
        const list = Array.from(root.querySelectorAll<HTMLElement>('[data-count]'));

        if (!(window as any).__countDebugOnce__) {
            (window as any).__countDebugOnce__ = true;
            console.log('[CountUp] found:', list.length, list);
        }

        return list;
    }

    function animate(el: HTMLElement) {
        if (el.classList.contains('is-counted')) return;

        const raw = (el.getAttribute('data-count') || '').trim();
        // Allow float
        const target = parseFloat(raw.replace(',', '.'));
        if (!Number.isFinite(target)) {
            console.warn('[CountUp] invalid data-count:', raw, el);
            return;
        }

        const prefix = el.getAttribute('data-prefix') ?? '';
        const suffix = el.getAttribute('data-suffix') ?? '';

        el.classList.add('is-counted');

        // bezpečně sestavit DOM (a hlavně neshodit úmyslnou mezeru v sufixu)
        el.textContent = '';

        if (prefix) {
            const p = document.createElement('span');
            p.className = 'u-prefix';
            p.textContent = prefix;
            el.appendChild(p);
        }

        const v = document.createElement('span');
        v.className = 'u-val';
        v.textContent = '0';
        el.appendChild(v);

        if (suffix) {
            const s = document.createElement('span');
            s.className = 'u-suffix';
            s.textContent = suffix;
            el.appendChild(s);
        }

        const span = v;


        const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

        // Determine decimal places from target or raw string
        const hasDecimals = raw.includes('.') || raw.includes(',');
        const isRating = el.closest('.HomeHero-stat--rating');
        const decimals = (hasDecimals || isRating) ? 1 : 0;

        const format = (n: number) => n.toLocaleString('cs-CZ', {
            minimumFractionDigits: decimals,
            maximumFractionDigits: decimals
        }).replace(',', '.');


        if (reduceMotion) {
            span.textContent = format(target);
            return;
        }

        const start = performance.now();
        const step = (now: number) => {
            const t = Math.min(1, (now - start) / DURATION);
            const val = target * easeOutQuad(t); // No rounding here yet
            span.textContent = format(val);
            if (t < 1) requestAnimationFrame(step);
            else span.textContent = format(target);
        };
        requestAnimationFrame(step);
    }


    function inViewport(el: Element) {
        const r = el.getBoundingClientRect();
        const vh = window.innerHeight || document.documentElement.clientHeight;
        const vw = window.innerWidth || document.documentElement.clientWidth;
        return r.bottom > 0 && r.right > 0 && r.top < vh && r.left < vw;
    }

    function run() {
        const targets = findTargets();
        if (!targets.length) return;
        targets.forEach((el) => {
            if (inViewport(el)) animate(el);
        });
    }

    const start = () => {
        run();
        setTimeout(run, 0);
        setTimeout(run, 150);
    };

    if (document.readyState !== 'loading') start();
    else document.addEventListener('DOMContentLoaded', start, { once: true });


    window.addEventListener('scroll', run, { passive: true });
    window.addEventListener('resize', run);
})();


// --------------------------------- Lightbox galleries (lightGallery + single images)
(() => {
    // 1. Standard Gutenberg Galleries
    const galleries = document.querySelectorAll<HTMLElement>('.wp-block-gallery');
    galleries.forEach(gallery => {
        lightGallery(gallery, {
            plugins: [lgThumbnail],
            selector: '.wp-block-image > a',
            thumbnail: true,
            speed: 300,
        });
    });

    // 2. Single images or images in columns linking to media files
    // Select all links that point to an image file and are NOT inside an already initialized gallery
    // Also EXCLUDE property-specific links that are handled separately below
    const excludeSelector = '.wp-block-gallery a, .PropertyTop-grid a, .PropertyBottomGallery-grid a, .PropertyDetails-floorplan';
    const singleImageLinks = document.querySelectorAll<HTMLElement>(
        `a[href$=".jpg"]:not(${excludeSelector}), a[href$=".jpeg"]:not(${excludeSelector}), a[href$=".png"]:not(${excludeSelector}), a[href$=".webp"]:not(${excludeSelector})`
    );

    if (singleImageLinks.length) {
        // Group them into one big gallery or keep separate? 
        // Best to group all single images on the page into one gallery for easy arrows (prepinani)
        const wrapper = document.body;
        lightGallery(wrapper, {
            plugins: [lgThumbnail],
            selector: `a[href$=".jpg"]:not(${excludeSelector}), a[href$=".jpeg"]:not(${excludeSelector}), a[href$=".png"]:not(${excludeSelector}), a[href$=".webp"]:not(${excludeSelector})`,
            thumbnail: true,
            speed: 300,
        });
    }
})();

(() => {
    // Property gallery — video (iframe) + photos in one lightGallery
    const videoTrigger = document.querySelector<HTMLElement>('[data-role="video-trigger"]');
    const links = Array.from(document.querySelectorAll<HTMLAnchorElement>(
        '.PropertyTop-grid > a, .PropertyBottomGallery-grid > a, .PropertyDetails-floorplan'
    ));
    if (!links.length && !videoTrigger) return;

    const dynamicEl: Array<{ src: string; thumb: string; iframe?: boolean }> = [];
    let videoOffset = 0;

    // Video as first slide (lightweight iframe, no lgVideo)
    if (videoTrigger) {
        const vSrc = videoTrigger.dataset.src || '';
        const vType = videoTrigger.dataset.type || '';

        let iframeSrc = vSrc;
        if (vType === 'youtube') {
            const sep = vSrc.includes('?') ? '&' : '?';
            iframeSrc = `${vSrc}${sep}autoplay=1&rel=0`;
        }

        // Extract a thumb for the video
        let vThumb = videoTrigger.dataset.thumb || '';
        if (vType === 'youtube') {
            const ytId = vSrc.split('/').pop()?.split('?')[0] || '';
            vThumb = `https://img.youtube.com/vi/${ytId}/hqdefault.jpg`;
            dynamicEl.push({ src: iframeSrc, thumb: vThumb, iframe: true });
        } else if (vType === 'file') {
            dynamicEl.push({ 
                thumb: vThumb, 
                poster: vThumb,
                video: {
                    source: [{ src: vSrc, type: 'video/mp4' }],
                    attributes: { preload: false, controls: true, playsinline: true, poster: vThumb, autoplay: true }
                }
            });
        } else {
            dynamicEl.push({ src: iframeSrc, thumb: vThumb, iframe: true });
        }
        
        videoOffset = 1;
    }

    // Photo slides
    links.forEach(link => {
        const img = link.querySelector('img');
        dynamicEl.push({
            src: link.href,
            thumb: img?.src || link.href,
        });
    });

    const wrapper = document.createElement('div');
    wrapper.style.display = 'none';
    document.body.appendChild(wrapper);

    const gallery = lightGallery(wrapper, {
        plugins: [lgThumbnail, lgVideo],
        thumbnail: true,
        speed: 300,
        dynamic: true,
        dynamicEl,
        closeOnTap: true,
        showCloseIcon: true,
    });

    // Video trigger opens lightGallery at index 0 (bypasses old modal)
    if (videoTrigger) {
        videoTrigger.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopImmediatePropagation();
            gallery.openGallery(0);
        }, true); // capture phase — runs before old modal handler
    }

    // Photo links open at their offset index
    links.forEach((link, i) => {
        link.addEventListener('click', (e) => {
            e.preventDefault();
            gallery.openGallery(i + videoOffset);
        });
    });
})();


//--------------------------------------------Investiční kalkulačka


(() => {
    const priceRange = document.querySelector('[data-role="price-range"]') as HTMLInputElement
    const loanRange = document.querySelector('[data-role="loan-range"]') as HTMLInputElement
    const maturityRange = document.querySelector('[data-role="maturity-range"]') as HTMLInputElement

    const priceInput = document.querySelector('[data-role="price-input"]') as HTMLInputElement
    const loanInput = document.querySelector('[data-role="loan-input"]') as HTMLInputElement
    const maturityInput = document.querySelector('[data-role="maturity-input"]') as HTMLInputElement

    const paymentDisplay = document.querySelector('[data-role="payment-display"]')

    // Hidden inputs for form (keeping names generic or as is to avoid breaking form handler if strictly coupled)
    const hiddenPrice = document.querySelector('[data-role="price-hidden"]') as HTMLInputElement
    const hiddenLoan = document.querySelector('[data-role="rent-hidden"]') as HTMLInputElement // reusing rent-hidden for loan
    const hiddenMaturity = document.querySelector('[data-role="annual-rent-hidden"]') as HTMLInputElement // reusing annual-rent for maturity
    const hiddenPayment = document.querySelector('[data-role="yield-hidden"]') as HTMLInputElement // reusing yield for payment

    const resultPanel = document.querySelector('[data-role="result-panel"]') as HTMLElement
    const sendButton = document.querySelector('[data-role="send-button"]') as HTMLButtonElement

    const modalOverlay = document.querySelector('[data-role="ic-modal"]') as HTMLElement
    const modalCloseBtns = document.querySelectorAll('[data-role="ic-modal-close"]')

    if (
        !priceRange ||
        !loanRange ||
        !maturityRange ||
        !priceInput ||
        !loanInput ||
        !maturityInput ||
        !resultPanel ||
        !sendButton ||
        !modalOverlay
    ) {
        return
    }

    const RATE = 4.59;

    const parseNumber = (value) => {
        return Number(String(value).replace(/\s+/g, '').replace(/Kč/g, '').replace(/let/g, '').replace(/\./g, '').replace(/,/g, '.')) || 0
    }

    const formatCurrency = (value) => {
        return value.toLocaleString('cs-CZ', { maximumFractionDigits: 0 }) + ' Kč'
    }

    const formatYears = (value) => {
        return value.toLocaleString('cs-CZ', { maximumFractionDigits: 0 }) + ' let'
    }

    const updateRangeTrack = (range) => {
        if (!range) return

        const min = Number(range.min) || 0
        const max = Number(range.max) || 100
        const value = Number(range.value)
        const percent = ((value - min) / (max - min)) * 100

        range.style.background =
            `linear-gradient(to right,
        var(--colorPrimary) 0%,
        var(--colorPrimary) ${percent}%,
        var(--colorSecondary) ${percent}%,
        var(--colorSecondary) 100%)`
    }

    const recalc = () => {
        const price = Number(priceRange.value)
        const loan = Number(loanRange.value)
        const years = Number(maturityRange.value)

        let monthlyPayment = 0;

        if (loan > 0 && years > 0) {
            const i = (RATE / 100) / 12;
            const n = years * 12;

            if (i === 0) {
                monthlyPayment = loan / n;
            } else {
                monthlyPayment = loan * (i * Math.pow(1 + i, n)) / (Math.pow(1 + i, n) - 1);
            }
        }

        if (paymentDisplay) paymentDisplay.textContent = formatCurrency(Math.round(monthlyPayment))

        if (hiddenPrice) hiddenPrice.value = String(price)
        if (hiddenLoan) hiddenLoan.value = String(loan)
        if (hiddenMaturity) hiddenMaturity.value = String(years)
        if (hiddenPayment) hiddenPayment.value = Math.round(monthlyPayment).toString()

        resultPanel.classList.add('is-calculated')
        sendButton.disabled = false
    }

    const syncInputsFromRanges = () => {
        const price = Number(priceRange.value)
        const loan = Number(loanRange.value)
        const years = Number(maturityRange.value)

        priceInput.value = formatCurrency(price)
        loanInput.value = formatCurrency(loan)
        maturityInput.value = formatYears(years)

        updateRangeTrack(priceRange)
        updateRangeTrack(loanRange)
        updateRangeTrack(maturityRange)

        recalc()
    }

    loanRange.value = String(Number(priceRange.value) * 0.8)
    syncInputsFromRanges()

    priceRange.addEventListener('input', () => {
        const price = Number(priceRange.value)
        loanRange.value = String(price * 0.8)
        syncInputsFromRanges()
    })
    loanRange.addEventListener('input', syncInputsFromRanges)
    maturityRange.addEventListener('input', syncInputsFromRanges)

    const handleInputBlur = (input: HTMLInputElement, range: HTMLInputElement, isYear = false) => {
        const raw = parseNumber(input.value)
        const min = Number(range.min)
        const max = Number(range.max)
        const value = Math.min(Math.max(raw || min, min), max)

        range.value = String(value)

        if (input === priceInput) {
            loanRange.value = String(value * 0.8)
        }

        syncInputsFromRanges()
    }

    priceInput.addEventListener('blur', () => handleInputBlur(priceInput, priceRange))
    loanInput.addEventListener('blur', () => handleInputBlur(loanInput, loanRange))
    maturityInput.addEventListener('blur', () => handleInputBlur(maturityInput, maturityRange, true))

    const openModal = () => {
        recalc()
        modalOverlay.classList.add('is-opened')
        modalOverlay.setAttribute('aria-hidden', 'false')
        document.body.classList.add('has-ic-modal-open')
    }

    const closeModal = () => {
        modalOverlay.classList.remove('is-opened')
        modalOverlay.setAttribute('aria-hidden', 'true')
        document.body.classList.remove('has-ic-modal-open')
    }

    sendButton.addEventListener('click', () => {
        if (sendButton.disabled) return
        openModal()
    })

    modalCloseBtns.forEach((btn) => {
        btn.addEventListener('click', closeModal)
    })

    modalOverlay.addEventListener('click', (e) => {
        if (e.target === modalOverlay) {
            closeModal()
        }
    })

    document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && modalOverlay.classList.contains('is-opened')) {
            closeModal()
        }
    })
})();

//-------------------ouško

(() => {

    const middle = document.querySelector<HTMLElement>(".Property-middle")
    const card = document.querySelector<HTMLElement>('[data-role="broker-card"]')

    if (!middle || !card) return


    const io = new IntersectionObserver(
        ([entry]) => {
            if (entry.isIntersecting) card.classList.remove("is-ear")
            else card.classList.add("is-ear")
        },
        {
            threshold: 0.05,
            rootMargin: "-20px 0px -20px 0px",
        }
    )

    io.observe(middle)
})();

//------------------
document.addEventListener("click", (e) => {
    const btn = (e.target as HTMLElement).closest<HTMLElement>("[data-open-lightbox]")
    if (!btn) return

    const group = btn.dataset.openLightbox
    if (!group) return

    const first = document.querySelector<HTMLElement>(`[data-lightbox="${CSS.escape(group)}"]`)
    first?.dispatchEvent(new MouseEvent("click", { bubbles: true, cancelable: true, view: window }))
});

//----------------------------- přepínání recenzí
(() => {
    const root = document.querySelector<HTMLElement>("[data-rating-tabs]")
    if (!root) return

    const tabs = Array.from(root.querySelectorAll<HTMLButtonElement>("[data-rating-tab]"))
    const panels = Array.from(root.querySelectorAll<HTMLElement>("[data-rating-panel]"))

    const setActive = (key: string) => {
        tabs.forEach((btn) => {
            const active = btn.dataset.ratingTab === key
            btn.classList.toggle("is-active", active)
            btn.setAttribute("aria-selected", active ? "true" : "false")
        })

        panels.forEach((p) => {
            const active = p.dataset.ratingPanel === key
            p.classList.toggle("is-active", active)
            p.toggleAttribute("hidden", !active)
        })
    }

    tabs.forEach((btn) => btn.addEventListener("click", () => setActive(btn.dataset.ratingTab || "firmy")))

    // default
    setActive("firmy")
})()

    //----------------------------- PropertyTop Video Modal
    ; (() => {
        const trigger = document.querySelector<HTMLElement>('[data-role="video-trigger"]')
        const modal = document.querySelector<HTMLElement>('[data-role="video-modal"]')
        if (!trigger || !modal) return

        const body = modal.querySelector<HTMLElement>('[data-role="video-modal-body"]')
        const closeBtn = modal.querySelector<HTMLElement>('[data-role="video-modal-close"]')
        if (!body || !closeBtn) return

        const type = trigger.dataset.type
        const src = trigger.dataset.src

        const closeModal = () => {
            modal.classList.remove('is-opened')
            modal.setAttribute('aria-hidden', 'true')
            document.body.classList.remove('u-hiddenOverflow')
            body.innerHTML = '' // Stop video
        }

        const openModal = () => {
            let html = ''

            if (type === 'youtube') {
                // Ensure autoplay and unmuted
                // Classic embed: https://www.youtube.com/embed/VIDEO_ID?params...
                // We append params. Assuming src includes parameters or not, we might need to be careful.
                // But usually raw embed link is just the base.
                // If src already has '?', assume it has some params.
                const separator = src?.includes('?') ? '&' : '?'
                const url = `${src}${separator}autoplay=1&mute=0&rel=0`

                html = `
                <iframe
                    class="PropertyTop-modalVideo"
                    src="${url}"
                    title="Video"
                    frameborder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowfullscreen
                ></iframe>
            `
            } else if (type === 'file') {
                const thumb = trigger.dataset.thumb || ''
                html = `
                <video class="PropertyTop-modalVideo" autoplay controls playsinline poster="${thumb}">
                    <source src="${src}">
                </video>
            `
            } else if (type === 'iframe') {
                const separator = src?.includes('?') ? '&' : '?'
                const url = `${src}${separator}autoplay=1&muted=0&background=0`

                html = `
                <iframe
                    class="PropertyTop-modalVideo"
                    src="${url}"
                    title="Video"
                    frameborder="0"
                    allow="autoplay; fullscreen"
                    allowfullscreen
                ></iframe>
            `
            }

            body.innerHTML = html
            modal.classList.add('is-opened')
            modal.setAttribute('aria-hidden', 'false')
            document.body.classList.add('u-hiddenOverflow')
        }

        trigger.addEventListener('click', (e) => {
            e.preventDefault()
            openModal()
        })

        closeBtn.addEventListener('click', closeModal)

        modal.addEventListener('click', (e) => {
            if (e.target === modal) closeModal()
        })

        document.addEventListener('keydown', (e) => {
            if (e.key === 'Escape' && modal.classList.contains('is-opened')) {
                closeModal()
            }
        })
    })()

//----------------------------- Reviews Slider (HP)
function initReviewsSection() {
    const sliders = document.querySelectorAll<HTMLElement>('[data-reviews-slider]');
    if (!sliders.length) return;

    sliders.forEach((slider) => {
        const wrapper = slider.querySelector<HTMLElement>('.swiper-wrapper');
        if (wrapper) applyReadMore(wrapper);

        const swiperInstance = new Swiper(slider, {
            modules: [Navigation],
            slidesPerView: 1,
            spaceBetween: 20,
            autoHeight: false,
            navigation: {
                prevEl: slider.querySelector<HTMLElement>('.HomeReviews-nav--prev'),
                nextEl: slider.querySelector<HTMLElement>('.HomeReviews-nav--next'),
            },
            breakpoints: {
                640: { slidesPerView: 2 },
                1024: { slidesPerView: 3 },
            },
        });

        if (wrapper) {
            wrapper.addEventListener('click', (e) => {
                if ((e.target as HTMLElement).classList.contains('ReviewCard-readMore')) {
                    setTimeout(() => swiperInstance.update(), 450);
                }
            });
        }
    });
}

//----------------------------- Read-more zkrácení textu recenzí
function applyReadMore(container: HTMLElement, wordLimit: number = 50) {
    const textEls = Array.from(container.querySelectorAll<HTMLElement>('.ReviewCard-text'));

    textEls.forEach(el => {
        // Zabráníme dvojí inicializaci na jedné kartě
        if (el.dataset.readMoreInit) return;
        el.dataset.readMoreInit = '1';

        const fullText = el.innerHTML.trim() ?? '';
        const plainText = el.textContent?.trim() ?? '';
        const words = plainText.split(/\s+/);

        if (words.length <= wordLimit) return; // krátký text

        // Vytvoříme strukturu pro animaci
        const wrapper = document.createElement('div');
        wrapper.className = 'ReviewCard-textValue is-static';
        wrapper.innerHTML = fullText; // Vždy vkládáme plný text - zkrácení/skrytí řeší CSS max-height a maska

        const toggle = document.createElement('button');
        toggle.type = 'button';
        toggle.className = 'ReviewCard-readMore';
        toggle.textContent = 'Zobrazit více';

        el.innerHTML = '';
        el.appendChild(wrapper);
        el.appendChild(toggle);

        let expanded = false;
        toggle.addEventListener('click', () => {
            expanded = !expanded;

            if (expanded) {
                wrapper.classList.remove('is-static');
                wrapper.classList.add('is-expanded');
                toggle.textContent = 'Zobrazit méně';
            } else {
                wrapper.classList.remove('is-expanded');
                wrapper.classList.add('is-static');
                toggle.textContent = 'Zobrazit více';
            }
        });
    });
}

//----------------------------- Reviews Archive Grid + client-side pagination
function initReviewsArchive() {
    const root = document.querySelector<HTMLElement>('[data-reviews-archive]');
    if (!root) return;

    const grid = root.querySelector<HTMLElement>('[data-reviews-archive-grid]');
    const pagerContainer = root.querySelector<HTMLElement>('[data-reviews-archive-pager]');
    const pageSize = parseInt(root.dataset.pageSize || '12', 10);

    if (!grid) return;

    applyReadMore(grid);

    const allCards = Array.from(grid.children) as HTMLElement[];
    if (allCards.length <= pageSize) return; // vše se vejde na jednu stránku

    let shown = pageSize;

    const applyVisibility = () => {
        allCards.forEach((card, i) => {
            card.style.display = i < shown ? '' : 'none';
        });
    };

    const renderPager = () => {
        if (!pagerContainer) return;
        const remaining = allCards.length - shown;
        if (remaining <= 0) {
            pagerContainer.innerHTML = '';
            return;
        }
        pagerContainer.innerHTML = `
            <div class="Pager u-frame" data-pager>
                <div class="Pager-spacing"></div>
                <div class="Pager-loadMore">
                    <div>
                        <a href="#" class="Button Button--primary" data-reviews-load-more>
                            <span>Načíst více</span>
                            <div class="ButtonCount Button--primaryIcon">
                                <span style="color: var(--colorDark)">${remaining}</span>
                                <div class="ButtonLoader" style="display:none"></div>
                            </div>
                        </a>
                    </div>
                </div>

            </div>
        `;
        pagerContainer.querySelector('[data-reviews-load-more]')?.addEventListener('click', (e) => {
            e.preventDefault();
            shown = Math.min(shown + pageSize, allCards.length);
            applyVisibility();
            renderPager();
        });
    };

    applyVisibility();
    renderPager();
}

function initComparisonSliders() {
    const sliders = document.querySelectorAll<HTMLElement>('.ComparisonSlider');
    sliders.forEach(slider => {
        const input = slider.querySelector<HTMLInputElement>('.ComparisonSlider-range');
        if (!input) return;

        input.addEventListener('input', () => {
            slider.style.setProperty('--rangeValue', `${input.value}%`);
        });
    });
}

function runInit() {
    initReviewsSection();
    initReviewsArchive();
    initComparisonSliders();
}

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', runInit);
} else {
    runInit();
}




